import { Button, Card, Flex, Form, Select, Space, Typography } from 'antd';
import PropTypes from 'prop-types';
import React from 'react';
import styled from 'styled-components';
import { QuestionProcessingType } from '../../../constants';
import { transcriptDetailsType } from '../../../types';
import CodeFrameSelect from './CodeFrameSelect';

const { Title, Paragraph, Text } = Typography;

const StyledParagraph = styled(Paragraph)`
  text-indent: 8px;
`;

const StyledTitle = styled(Title)`
  margin-top: 0;
`;

const StyledFormItem = styled(Form.Item)`
  margin-bottom: 8px;
`;

function CodingGuidance({
  transcriptData,
  prev,
  createTranscriptLoading,
  finishUpload,
  setTranscriptData,
}) {
  const [form] = Form.useForm();
  const questions = transcriptData.questions || [];

  // IDI's are only available for PRO processing.
  const isProProcessing = true;

  const onFinish = () => {
    finishUpload();
  };

  const handleProcessingTypeChange = (value, questionIndex) => {
    const updatedQuestions = [...transcriptData.questions];
    updatedQuestions[questionIndex] = {
      ...updatedQuestions[questionIndex],
      processing_type: value,
      ...(value !== QuestionProcessingType.REUSE_CODE_FRAME && { import_code_frame_id: null }),
    };
    setTranscriptData({
      ...transcriptData,
      questions: updatedQuestions,
    });
  };

  const handleCodeFrameSelect = (codeFrameId, questionIndex) => {
    const updatedQuestions = [...transcriptData.questions];
    updatedQuestions[questionIndex] = {
      ...updatedQuestions[questionIndex],
      import_code_frame_id: codeFrameId,
    };
    setTranscriptData({
      ...transcriptData,
      questions: updatedQuestions,
    });
  };

  return (
    <>
      <StyledTitle level={3}>Select a coding method for each question</StyledTitle>
      <Paragraph>Choose how you want to process each question from your interview guide.</Paragraph>
      <Form onFinish={onFinish} form={form} layout="vertical" requiredMark={false}>
        <Space direction="vertical" size="middle" style={{ width: '100%' }}>
          {questions.map((question, index) => {
            const questionProcessingType =
              question.processing_type || QuestionProcessingType.AI_GENERATED;

            return (
              <Card key={question.index} size="small">
                <Flex vertical gap="small">
                  <Text strong>{question.text}</Text>
                  <StyledFormItem
                    label="How should we process this question?"
                    name={`processing_type_${question.index}`}
                    initialValue={questionProcessingType}
                  >
                    <Select
                      placeholder="Select processing method"
                      onChange={(value) => handleProcessingTypeChange(value, index)}
                      options={[
                        {
                          value: QuestionProcessingType.REUSE_CODE_FRAME,
                          label: 'Reapply from past project',
                        },
                        isProProcessing && {
                          value: QuestionProcessingType.SKIP,
                          label: 'Enter/provide own code frame',
                        },
                        {
                          value: QuestionProcessingType.AI_GENERATED,
                          label: 'Generate from scratch',
                        },
                      ].filter(Boolean)}
                    />
                  </StyledFormItem>

                  {questionProcessingType === QuestionProcessingType.REUSE_CODE_FRAME && (
                    <StyledFormItem
                      label="Which code frame do you want to reuse?"
                      name={`code_frame_${question.index}`}
                      rules={[{ required: true, message: 'Please select a code frame' }]}
                      initialValue={question.import_code_frame_id}
                    >
                      <CodeFrameSelect onChange={(value) => handleCodeFrameSelect(value, index)} />
                    </StyledFormItem>
                  )}

                  {questionProcessingType === QuestionProcessingType.SKIP && (
                    <StyledParagraph type="secondary">
                      <Text italic>
                        You&apos;ll be able to manually enter your code frame after upload is
                        complete.
                      </Text>
                    </StyledParagraph>
                  )}

                  {questionProcessingType === QuestionProcessingType.AI_GENERATED && (
                    <StyledParagraph type="secondary">
                      <Text italic>
                        AI will automatically generate a code frame based on the transcript content.
                      </Text>
                    </StyledParagraph>
                  )}
                </Flex>
              </Card>
            );
          })}
        </Space>

        <Form.Item style={{ marginTop: 24 }}>
          <Flex justify="space-between">
            <Button onClick={prev}>Back</Button>
            <Button type="primary" htmlType="submit" loading={createTranscriptLoading}>
              Upload transcripts
            </Button>
          </Flex>
        </Form.Item>
      </Form>
    </>
  );
}

CodingGuidance.propTypes = {
  transcriptData: transcriptDetailsType.isRequired,
  prev: PropTypes.func.isRequired,
  createTranscriptLoading: PropTypes.bool.isRequired,
  finishUpload: PropTypes.func.isRequired,
  setTranscriptData: PropTypes.func.isRequired,
};

export default CodingGuidance;
